import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { FileText, Users, Bell, CheckCircle, ArrowRight, Zap, Mail, Clock, Shield, CreditCard, Settings2 } from "lucide-react";

const LandingPage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-sand">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-forest rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-xl font-semibold text-forest">ProspectConnect</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Button asChild className="bg-forest hover:bg-forest/90">
                <Link to="/dashboard" data-testid="go-to-dashboard-btn">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/login" data-testid="login-btn">Sign In</Link>
                </Button>
                <Button asChild className="bg-forest hover:bg-forest/90">
                  <Link to="/register" data-testid="get-started-btn">Get Started</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fadeIn">
              <p className="font-label text-sm uppercase tracking-widest text-terracotta mb-4">For Advisory Firms</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-forest leading-tight mb-6">
                From Prospect to Client, Simplified
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                Save time with streamlined proposals, contracts, and payments—all in one place. Built for advisors who value professionalism and compliance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-forest hover:bg-forest/90 px-8 py-6 text-lg">
                  <Link to="/register" data-testid="hero-cta-btn">
                    Start Creating Proposals
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-2 border-forest text-forest hover:bg-forest/5 px-8 py-6 text-lg">
                  <Link to="/login">Sign In</Link>
                </Button>
              </div>
            </div>
            <div className="relative animate-fadeIn stagger-2">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-stone">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-forest" />
                  </div>
                  <div>
                    <p className="font-label text-xs uppercase tracking-wider text-muted-foreground">Proposal</p>
                    <h3 className="font-display text-lg font-semibold text-forest">Financial Advisory Services</h3>
                  </div>
                </div>
                <div className="space-y-4 mb-6">
                  <div className="h-4 bg-sand rounded w-3/4"></div>
                  <div className="h-4 bg-sand rounded w-full"></div>
                  <div className="h-4 bg-sand rounded w-5/6"></div>
                </div>
                <div className="flex items-center justify-between p-4 bg-sand rounded-lg">
                  <span className="font-mono text-xl font-semibold text-forest">$15,000</span>
                  <Button size="sm" className="bg-terracotta hover:bg-terracotta/90">Accept</Button>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-terracotta text-white p-4 rounded-xl shadow-lg">
                <CheckCircle className="w-8 h-8" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props - Quick hits */}
      <section className="py-12 bg-white border-y border-stone">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Clock, text: "Save Time on Paperwork" },
              { icon: Zap, text: "Auto-Send on Accept" },
              { icon: Settings2, text: "Flexible Workflows" },
              { icon: CreditCard, text: "Payment Options" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-forest/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-forest" />
                </div>
                <span className="text-sm font-medium text-forest">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-label text-sm uppercase tracking-widest text-terracotta mb-4">Features</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-forest">Streamline Your Client Onboarding</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: "Professional Proposals",
                description: "Build branded proposals with customizable sections, pricing tables, and your firm's look and feel."
              },
              {
                icon: Users,
                title: "Simple Prospect Actions",
                description: "Prospects can review, accept, decline, or request a meeting—all from one clear interface."
              },
              {
                icon: Bell,
                title: "Stay Informed",
                description: "Get notified via email and in-app whenever prospects take action. No more guessing."
              },
              {
                icon: CheckCircle,
                title: "Contracts via DocuSign",
                description: "Automatically send agreements for signature when prospects accept. One less step for you."
              },
              {
                icon: CreditCard,
                title: "Flexible Payments",
                description: "One-time fees, split payments, or subscriptions. Send invoices or payment links via Stripe."
              },
              {
                icon: Shield,
                title: "Security You'd Expect",
                description: "Encrypted data, secure login, and session management. The basics done right."
              }
            ].map((feature, idx) => (
              <div 
                key={idx} 
                className="p-6 rounded-xl border border-stone hover:border-forest/30 transition-colors animate-fadeIn"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="w-12 h-12 bg-forest/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-forest" />
                </div>
                <h3 className="font-display text-xl font-semibold text-forest mb-2">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-sand">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-label text-sm uppercase tracking-widest text-terracotta mb-4">How It Works</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-forest">Three Steps to a New Client</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Create Your Proposal",
                description: "Add your services, pricing, and any supporting documents. Customize it to fit the prospect."
              },
              {
                step: "2",
                title: "Send & Track",
                description: "Email the proposal directly from the app. Track when it's viewed and get notified on actions."
              },
              {
                step: "3",
                title: "Close & Onboard",
                description: "When they accept, send contracts and invoices automatically. Move from prospect to client smoothly."
              }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 bg-forest text-white rounded-full flex items-center justify-center mx-auto mb-4 font-display text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-display text-xl font-semibold text-forest mb-2">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-forest">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to Simplify Your Workflow?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Spend less time on paperwork and more time with clients. Start creating professional proposals today.
          </p>
          <Button asChild size="lg" className="bg-terracotta hover:bg-terracotta/90 px-8 py-6 text-lg">
            <Link to="/register" data-testid="footer-cta-btn">
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-sand border-t border-stone">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-forest rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <span className="font-display text-lg font-semibold text-forest">ProspectConnect</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="w-4 h-4" />
                <a href="mailto:info@prospectconnect.com" className="hover:text-forest transition-colors">
                  info@prospectconnect.com
                </a>
              </div>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-forest transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-muted-foreground hover:text-forest transition-colors">
                Terms of Service
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} ProspectConnect. Built for advisory firms.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
